import clsx from 'clsx';

/**
 * Issue #221 / #222 / #224 — 分区卡片 / Segmented 标签栏。
 *
 * 知识库、系统设置、日志模块都要求「一次只展示一个版块，切换清晰」。把切换条抽成一个
 * 受控的纯展示组件：父组件持有当前选中的 key（见 lib/page-tabs.ts 的纯 helper），这里
 * 只负责渲染按钮 + 触发 onChange。无动态 Tailwind 类名拼接（架构红线 6.2.2）——选中态
 * 用静态 class 二选一。
 */
export interface SegmentedTabItem<K extends string = string> {
  key: K;
  label: string;
  /** 可选计数徽标（如日志条数）。 */
  badge?: number;
}

export interface SegmentedTabsProps<K extends string = string> {
  items: ReadonlyArray<SegmentedTabItem<K>>;
  value: K;
  onChange: (key: K) => void;
  className?: string;
  'data-testid'?: string;
}

export function SegmentedTabs<K extends string = string>({
  items,
  value,
  onChange,
  className,
  'data-testid': testId,
}: SegmentedTabsProps<K>) {
  return (
    <div
      role="tablist"
      aria-label="分区切换"
      data-testid={testId}
      className={clsx(
        'inline-flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.key === value;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.key)}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition',
              active
                ? 'bg-white text-brand-600 shadow-sm ring-1 ring-slate-200'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {item.label}
            {typeof item.badge === 'number' ? (
              <span
                className={clsx(
                  'rounded-full px-1.5 py-0.5 text-[10px]',
                  active ? 'bg-brand-50 text-brand-600' : 'bg-slate-200 text-slate-500',
                )}
              >
                {item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
